/* Common Imports */

import React from "react";
import { styled } from "@mui/system";

/* MUI Imports */

import {
	Checkbox,
	Chip,
	FormControlLabel,
	InputAdornment,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";

/* Component Imports */

import LocationAutocompleteMulti from "./LocationAutocompleteMulti";

/* Icon Imports */

import CottageOutlinedIcon from "@mui/icons-material/CottageOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import AgricultureOutlinedIcon from "@mui/icons-material/AgricultureOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";

/* Styled Components */

const Container = styled("div")(() => ({
	display: "flex",
	flexDirection: "column",
	gap: "1.5rem",
}));

const SectionTitle = styled(Typography)(() => ({
	fontSize: "1rem",
	fontWeight: 600,
	color: "#212121",
}));

const ChipGroup = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	gap: "0.5rem",
	flexWrap: "wrap",
}));

const CategoryGrid = styled("div")(() => ({
	display: "grid",
	gridTemplateColumns: "repeat(3, 1fr)",
	gap: "0.75rem",
}));

const CategoryCard = styled("div")<{ selected?: boolean }>(({ selected }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "flex-start",
	padding: "0.75rem",
	borderRadius: "0.5rem",
	border: selected ? "2px solid #E07934" : "1px solid #e0e0e0",
	backgroundColor: selected ? "#FFF8F3" : "#fff",
	cursor: "pointer",
	gap: "0.5rem",
	position: "relative",
	"&:hover": {
		borderColor: "#E07934",
	},
}));

const RadioDot = styled("div")<{ selected?: boolean }>(({ selected }) => ({
	position: "absolute",
	top: "0.5rem",
	right: "0.5rem",
	width: "1rem",
	height: "1rem",
	borderRadius: "50%",
	border: selected ? "5px solid #E07934" : "2px solid #bdbdbd",
	backgroundColor: "#fff",
}));

const FieldRow = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	gap: "1rem",
	width: "100%",
}));

/* Property type options by category */

const propertyTypeOptions: Record<string, Array<string>> = {
	residential: ["Apartment", "Independent House", "Villa", "Plot", "Land (Residential)", "Co-living/PG", "Row House", "Builder Floor Apartment", "Penthouse", "Studio Apartment", "Building (Residential)"],
	commercial: ["Building (Commercial)", "Land (Commercial)", "Shop", "Office Space", "Co-working", "Plot (Commercial)"],
	agricultural: ["Land (Agricultural)", "Farm House", "Farm Plot"],
	industrial: ["Land (Industrial)", "Factory", "Mining"],
	warehousing: ["Warehouse"],
};

/* BHK options */

const bhkOptions = ["1 RK", "1 BHK", "2 BHK", "2.5 BHK", "+3 BHK"];

/* Plot dimension presets */

const plotDimensionOptions = ["20x30", "30x40", "30x50", "40x60", "50x80", "60x90", "80x120", "Custom"];

/* Config groups — types within the same group share the same config fields */

const configGroups: Record<string, string[]> = {
	unit: ["Apartment", "Independent House", "Villa", "Row House", "Penthouse", "Co-living/PG", "Builder Floor Apartment", "Farm House"],
	plot: ["Plot", "Farm Plot", "Plot (Commercial)"],
	land: ["Land (Residential)", "Land (Commercial)", "Land (Agricultural)", "Land (Industrial)"],
	commercial_unit: ["Shop", "Office Space"],
	building: ["Building (Residential)", "Building (Commercial)"],
	studio: ["Studio Apartment"],
	coworking: ["Co-working"],
	industrial: ["Warehouse", "Factory", "Mining"],
};

const getConfigGroup = (propertyType: string): string | null => {
	for (const [group, types] of Object.entries(configGroups)) {
		if (types.includes(propertyType)) return group;
	}
	return null;
};

const getActiveConfigGroup = (selectedTypes: string[]): string | null => {
	if (selectedTypes.length === 0) return null;
	return getConfigGroup(selectedTypes[0]);
};

const isTypeDisabled = (propertyType: string, selectedTypes: string[]): boolean => {
	if (selectedTypes.length === 0) return false;
	const activeGroup = getActiveConfigGroup(selectedTypes);
	if (!activeGroup) return false;
	return getConfigGroup(propertyType) !== activeGroup;
};

/* Category config */

const categories = [
	{ key: "residential", label: "Residential", icon: CottageOutlinedIcon },
	{ key: "commercial", label: "Commercial", icon: BusinessOutlinedIcon },
	{ key: "agricultural", label: "Agriculture", icon: AgricultureOutlinedIcon },
	{ key: "warehousing", label: "Warehouse", icon: WarehouseOutlinedIcon },
	{ key: "industrial", label: "Industrial", icon: FactoryOutlinedIcon },
];

/* Component */

const PropertyRequirementStep = ({
	formData,
	updateFormData,
}: {
	formData: any;
	updateFormData: (field: string, value: any) => void;
}) => {
	const isJdJv = formData.transaction_type === "jd" || formData.transaction_type === "jv";

	/* Config field visibility */

	const noBhk = ["Plot", "Land (Residential)", "Land (Commercial)", "Land (Agricultural)", "Land (Industrial)", "Studio Apartment", "Building (Commercial)", "Building (Residential)", "Shop", "Office Space", "Co-working", "Warehouse", "Factory", "Mining", "Farm Plot", "Plot (Commercial)"];
	const plotTypes = ["Plot", "Farm Plot", "Plot (Commercial)"];
	const seatsTypes = ["Co-working"];
	const types: string[] = formData.property_type || [];
	const hasType = (list: string[]) => types.some((t: string) => list.includes(t));
	const showBhk = types.length > 0 && !hasType(noBhk) && !isJdJv;
	const showSeats = hasType(seatsTypes);
	const showPlotDimensions = hasType(plotTypes);
	const acreTypes = ["Land (Residential)", "Land (Commercial)", "Land (Agricultural)", "Land (Industrial)", "Farm House", "Farm Plot", "Mining"];
	const isCustomPlot = (formData.configuration.plot_dimensions || []).includes("Custom");
	const showArea = !showPlotDimensions || isCustomPlot;
	const defaultAreaUnit = hasType(acreTypes) ? "acre" : "sqft";

	/* Handle property type multi-select toggle */

	const handlePropertyTypeToggle = (type: string) => {
		const current: string[] = formData.property_type || [];
		if (current.includes(type)) {
			updateFormData("property_type", current.filter((t: string) => t !== type));
		} else {
			if (isTypeDisabled(type, current)) return;
			updateFormData("property_type", [...current, type]);
		}
	};

	/* Handle plot dimension selection */

	const handlePlotDimensionToggle = (dim: string) => {
		const current: string[] = formData.configuration.plot_dimensions || [];
		if (dim === "Custom") {
			if (current.includes("Custom")) {
				updateFormData("configuration.plot_dimensions", []);
				updateFormData("configuration.area_min", 0);
				updateFormData("configuration.area_max", 0);
			} else {
				updateFormData("configuration.plot_dimensions", ["Custom"]);
			}
		} else {
			if (current.includes(dim)) {
				updateFormData("configuration.plot_dimensions", current.filter((d: string) => d !== dim));
			} else {
				updateFormData("configuration.plot_dimensions", [...current.filter((d: string) => d !== "Custom"), dim]);
				updateFormData("configuration.area_min", 0);
				updateFormData("configuration.area_max", 0);
			}
		}
	};

	/* Handle BHK selection */

	const handleBhkSelect = (bhk: string) => {
		const bhkValue = bhk === "1 RK" ? 0.5 : bhk === "+3 BHK" ? 3 : parseFloat(bhk);
		const currentBhk: number[] = Array.isArray(formData.configuration.bhk) ? formData.configuration.bhk : [];
		const newBhk = currentBhk.includes(bhkValue)
			? currentBhk.filter((b: number) => b !== bhkValue)
			: [...currentBhk, bhkValue];
		updateFormData("configuration.bhk", newBhk);
	};

	return (
		<Container>
			{/* Transaction Type */}

			<div>
				<SectionTitle>
					What are you looking for? <span style={{ color: "#E07934" }}>*</span>
				</SectionTitle>
				<ChipGroup sx={{ marginTop: "0.75rem" }}>
					{["buy", "rent"].map((type) => (
						<Chip
							key={type}
							label={type === "buy" ? "Sale" : type.charAt(0).toUpperCase() + type.slice(1)}
							variant={formData.transaction_type === type ? "filled" : "outlined"}
							onClick={() => updateFormData("transaction_type", type)}
							sx={{
								backgroundColor: formData.transaction_type === type ? "#FFF8F3" : "transparent",
								borderColor: formData.transaction_type === type ? "#E07934" : "#e0e0e0",
								color: formData.transaction_type === type ? "#E07934" : "#424242",
								fontWeight: 500,
							}}
						/>
					))}
				</ChipGroup>
			</div>

			{/* Property Category */}

			<div>
				<SectionTitle>
					{isJdJv ? "What type of land are you looking for?" : "What kind of property is this?"}{" "}
					<span style={{ color: "#E07934" }}>*</span>
				</SectionTitle>
				<CategoryGrid sx={{ marginTop: "0.75rem" }}>
					{categories.map((cat) => (
						<CategoryCard
							key={cat.key}
							selected={formData.property_category === cat.key}
							onClick={() => updateFormData("property_category", cat.key)}
						>
							<cat.icon sx={{ fontSize: "1.25rem", color: formData.property_category === cat.key ? "#E07934" : "#616161" }} />
							<Typography
								sx={{
									fontSize: "0.875rem",
									fontWeight: 500,
									color: formData.property_category === cat.key ? "#E07934" : "#424242",
								}}
							>
								{cat.label}
							</Typography>
							<RadioDot selected={formData.property_category === cat.key} />
						</CategoryCard>
					))}
				</CategoryGrid>
			</div>

			{/* Preferences Section */}

			{formData.property_category && (
				<div>
					<SectionTitle>Add your preferences</SectionTitle>

					{/* Property Types — multi-select with config grouping */}

					{!isJdJv && (
						<div style={{ marginTop: "0.75rem" }}>
							<Typography sx={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: "0.25rem" }}>
								Property Types <span style={{ color: "#E07934" }}>*</span>
							</Typography>
							<Typography sx={{ fontSize: "0.75rem", color: "#9e9e9e", marginBottom: "0.5rem" }}>
								Select one or more property types
							</Typography>
							<ChipGroup>
								{(propertyTypeOptions[formData.property_category] || []).map((type) => {
									const selected = (formData.property_type || []).includes(type);
									const disabled = isTypeDisabled(type, formData.property_type || []);
									return (
										<Chip
											key={type}
											label={type}
											variant={selected ? "filled" : "outlined"}
											onClick={() => !disabled && handlePropertyTypeToggle(type)}
											onDelete={selected ? () => handlePropertyTypeToggle(type) : undefined}
											sx={{
												backgroundColor: selected ? "#FFF8F3" : disabled ? "#f5f5f5" : "transparent",
												borderColor: selected ? "#E07934" : disabled ? "#e0e0e0" : "#e0e0e0",
												color: selected ? "#E07934" : disabled ? "#bdbdbd" : "#424242",
												fontWeight: 500,
												opacity: disabled ? 0.5 : 1,
												cursor: disabled ? "not-allowed" : "pointer",
											}}
										/>
									);
								})}
							</ChipGroup>
						</div>
					)}

					{/* BHK Configuration */}

					{showBhk && (
						<div style={{ marginTop: "1rem" }}>
							<Typography sx={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: "0.5rem" }}>
								Configuration <span style={{ color: "#E07934" }}>*</span>
							</Typography>
							<ChipGroup>
								{bhkOptions.map((bhk) => {
									const bhkValue = bhk === "1 RK" ? 0.5 : bhk === "+3 BHK" ? 3 : parseFloat(bhk);
									const isSelected = Array.isArray(formData.configuration.bhk) && formData.configuration.bhk.includes(bhkValue);
									return (
										<Chip
											key={bhk}
											label={bhk}
											variant={isSelected ? "filled" : "outlined"}
											onClick={() => handleBhkSelect(bhk)}
											sx={{
												backgroundColor: isSelected ? "#FFF8F3" : "transparent",
												borderColor: isSelected ? "#E07934" : "#e0e0e0",
												color: isSelected ? "#E07934" : "#424242",
												fontWeight: 500,
											}}
										/>
									);
								})}
							</ChipGroup>
						</div>
					)}

					{/* Seats (Co-working) */}

					{showSeats && (
						<div style={{ marginTop: "1rem" }}>
							<Typography sx={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: "0.25rem" }}>
								Number of Seats
							</Typography>
							<TextField
								size="small"
								type="number"
								placeholder="50"
								value={formData.configuration.seats || ""}
								onChange={(e) => updateFormData("configuration.seats", Number(e.target.value))}
								sx={{ width: "50%" }}
							/>
						</div>
					)}

					{/* Plot Dimensions */}

					{showPlotDimensions && (
						<div style={{ marginTop: "1rem" }}>
							<Typography sx={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: "0.5rem" }}>
								Plot Dimensions
							</Typography>
							<ChipGroup>
								{plotDimensionOptions.map((dim) => {
									const isSelected = (formData.configuration.plot_dimensions || []).includes(dim);
									return (
										<Chip
											key={dim}
											label={dim === "Custom" ? "Custom Area" : dim}
											variant={isSelected ? "filled" : "outlined"}
											onClick={() => handlePlotDimensionToggle(dim)}
											sx={{
												backgroundColor: isSelected ? "#FFF8F3" : "transparent",
												borderColor: isSelected ? "#E07934" : "#e0e0e0",
												color: isSelected ? "#E07934" : "#424242",
												fontWeight: 500,
											}}
										/>
									);
								})}
							</ChipGroup>
						</div>
					)}

					{/* Area */}

					{showArea && types.length > 0 && (
						<div style={{ marginTop: "1rem" }}>
							<Typography sx={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: "0.25rem" }}>
								{isCustomPlot ? "Custom Area" : "Total Area"}
							</Typography>
							<FieldRow>
								<TextField
									size="small"
									type="number"
									placeholder="Min"
									value={formData.configuration.area_min || ""}
									onChange={(e) => updateFormData("configuration.area_min", Number(e.target.value))}
									sx={{ flex: 1 }}
								/>
								<TextField
									size="small"
									type="number"
									placeholder="Max"
									value={formData.configuration.area_max || ""}
									onChange={(e) => updateFormData("configuration.area_max", Number(e.target.value))}
									sx={{ flex: 1 }}
								/>
								<Select
									size="small"
									value={formData.configuration.area_unit || defaultAreaUnit}
									onChange={(e) => updateFormData("configuration.area_unit", e.target.value)}
									sx={{ width: "5rem" }}
								>
									<MenuItem value="sqft">sqft</MenuItem>
									<MenuItem value="acre">acre</MenuItem>
								</Select>
							</FieldRow>
						</div>
					)}

					{/* Location */}

					<div style={{ marginTop: "1rem" }}>
						<Typography sx={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: "0.25rem" }}>
							Location <span style={{ color: "#E07934" }}>*</span>
						</Typography>
						<Typography sx={{ fontSize: "0.75rem", color: "#9e9e9e", marginBottom: "0.5rem" }}>
							You can add multiple locations to widen your search
						</Typography>
						<LocationAutocompleteMulti
							selectedLocations={formData.locations}
							onLocationsChange={(locations) => updateFormData("locations", locations)}
						/>
					</div>

					{/* Budget */}

					<div style={{ marginTop: "1rem" }}>
						<Typography sx={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: "0.5rem" }}>
							{formData.transaction_type === "rent"
								? "What's your expected monthly budget?"
								: "How much is your budget?"}{" "}
							<span style={{ color: "#E07934" }}>*</span>
						</Typography>
						<TextField
							size="small"
							type="number"
							placeholder="10,00,000"
							value={formData.budget.max || ""}
							onChange={(e) => {
								const val = Number(e.target.value);
								updateFormData("budget", { ...formData.budget, max: val, min: val });
							}}
							InputProps={{
								startAdornment: <InputAdornment position="start">₹</InputAdornment>,
							}}
							sx={{ width: "50%" }}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={formData.budget.is_flexible}
									onChange={(e) => updateFormData("budget.is_flexible", e.target.checked)}
									size="small"
								/>
							}
							label={
								<Typography sx={{ fontSize: "0.813rem", color: "#616161" }}>
									I&apos;m flexible with my budget
								</Typography>
							}
							// display:flex forces the checkbox onto its own line below the
							// amount field — MUI's FormControl (the TextField, width 50%) and
							// FormControlLabel are both inline-flex, so otherwise the checkbox
							// floats to the right of the input on wider viewports.
							// width:fit-content keeps the click area to the checkbox + label.
							sx={{ marginTop: "0.25rem", display: "flex", width: "fit-content" }}
						/>
					</div>

					{/* Notes for JD/JV */}

					{isJdJv && (
						<div style={{ marginTop: "1rem" }}>
							<Typography sx={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: "0.5rem" }}>
								Notes
							</Typography>
							<TextField
								fullWidth
								multiline
								rows={4}
								placeholder="Eg. prefer a quiet residential area with parking and power backup"
								value={formData.notes}
								onChange={(e) => updateFormData("notes", e.target.value)}
								inputProps={{ maxLength: 1000 }}
							/>
						</div>
					)}
				</div>
			)}
		</Container>
	);
};

export default PropertyRequirementStep;
